class FacetsOpenDesktop extends HTMLElement {
  connectedCallback() {
    this._mql = window.matchMedia('(min-width: 750px)');
    this._onMediaChange = this._onMediaChange.bind(this);
    this._onInput = this._onInput.bind(this);
    this._onHoverEnter = this._onHoverEnter.bind(this);
    this._onHoverLeave = this._onHoverLeave.bind(this);
    this._onToggle = this._onToggle.bind(this);
    this._onReposition = this._onReposition.bind(this);

    this._mql.addEventListener('change', this._onMediaChange);
    // mouseenter/mouseleave/toggle don't bubble, but a capture-phase listener on this ancestor
    // still sees them on the way down to the real target — delegating here (instead of binding a
    // controller per hover/panel) means an AJAX facet-count refresh that replaces this element's
    // inner markup just keeps working, with nothing to reattach.
    this.addEventListener('input', this._onInput, true);
    this.addEventListener('mouseenter', this._onHoverEnter, true);
    this.addEventListener('mouseleave', this._onHoverLeave, true);
    this.addEventListener('toggle', this._onToggle, true);

    FacetsOpenDesktop.syncDuplicateFacetInputs();
  }

  disconnectedCallback() {
    this._mql.removeEventListener('change', this._onMediaChange);
    this.removeEventListener('input', this._onInput, true);
    this.removeEventListener('mouseenter', this._onHoverEnter, true);
    this.removeEventListener('mouseleave', this._onHoverLeave, true);
    this.removeEventListener('toggle', this._onToggle, true);
    window.removeEventListener('scroll', this._onReposition, true);
    clearTimeout(this._closeTimer);
  }

  _onHoverEnter(event) {
    const hover = event.target.closest?.('.facets-open-inline__hover');
    const panel = hover?.querySelector('.facets-open-inline__panel');
    if (!panel) return;
    clearTimeout(this._closeTimer);
    if (panel.matches(':popover-open')) return;
    panel.showPopover();
  }

  _onHoverLeave(event) {
    const hover = event.target.closest?.('.facets-open-inline__hover');
    // Once shown, the panel is promoted to the top layer, which breaks :hover AND :focus-within
    // propagation from it back up to this DOM ancestor (verified: hovering, or focusing a
    // checkbox in, the open panel makes neither pseudo-class match on `.facets-open-inline__hover`
    // anymore) — relatedTarget/activeElement containment are plain DOM checks, unaffected by that,
    // and are the same technique quantity-popover.js already uses for this exact problem.
    if (!hover || hover.contains(event.relatedTarget) || hover.contains(document.activeElement)) return;
    const panel = hover.querySelector('.facets-open-inline__panel');
    if (!panel?.matches(':popover-open')) return;
    // A brief grace period before actually closing — real mouse paths aren't pixel-perfect, and
    // this tolerates a moment spent just outside both boxes while crossing between them, instead
    // of requiring the pointer to stay exactly within the trigger/panel/gap the whole way.
    clearTimeout(this._closeTimer);
    this._closeTimer = setTimeout(() => panel.hidePopover(), 200);
  }

  // Single source of truth for open/close bookkeeping — fires for every trigger (hover above,
  // click via the trigger's popovertarget, keyboard Enter/Space, native Escape, native
  // click-outside light-dismiss), so aria-expanded and positioning stay correct regardless of how
  // the popover was opened or closed.
  _onToggle(event) {
    const panel = event.target;
    if (!panel.matches?.('.facets-open-inline__panel')) return;

    const hover = panel.closest('.facets-open-inline__hover');
    const trigger = hover?.querySelector('.facets-open-inline__trigger');
    const isOpen = event.newState === 'open';
    trigger?.setAttribute('aria-expanded', String(isOpen));

    if (isOpen) {
      this._openHover = hover;
      this._openPanel = panel;
      this._position(hover, panel);
      window.addEventListener('scroll', this._onReposition, true);
    } else {
      this._openHover = null;
      this._openPanel = null;
      window.removeEventListener('scroll', this._onReposition, true);
      // Closing (via Escape or light-dismiss) restores focus to whatever was focused before the
      // panel opened — <body>, since opening via hover never focuses anything — which runs after
      // this event handler, so it would undo a focus() called here; defer to run after it instead.
      setTimeout(() => {
        if (document.activeElement === document.body) trigger?.focus();
      });
    }
  }

  _onReposition() {
    if (this._openHover && this._openPanel) this._position(this._openHover, this._openPanel);
  }

  // Manual positioning instead of CSS Anchor Positioning (anchor-name/position-anchor) — cross-browser
  // support was too limited at the time this was written. Revisit migrating to it later.
  _position(hover, panel) {
    const rect = hover.getBoundingClientRect();
    panel.style.top = `${rect.bottom}px`;
    panel.style.left = `${rect.left}px`;
  }

  _onMediaChange() {
    FacetsOpenDesktop.syncDuplicateFacetInputs();
  }

  _onInput(event) {
    const input = event.target;
    if (!input.matches || !input.matches('[data-open-facet-search]')) return;

    // This only filters the already-loaded list client-side — it must never reach
    // <facet-filters-form>'s debounced auto-submit, which would refetch the section and replace
    // this very input (mid-typing) with a fresh, empty one.
    event.stopPropagation();

    const panel = input.closest('.facets-open-inline__panel-inner');
    if (!panel) return;

    const list = panel.querySelector('[data-open-facet-list]');
    if (!list) return;

    const q = input.value.trim().toLowerCase();
    list.querySelectorAll('[data-open-facet-item]').forEach((li) => {
      const t = (li.dataset.searchText || '').toLowerCase();
      li.hidden = q.length > 0 && !t.includes(q);
    });
  }

  static syncDuplicateFacetInputs() {
    const desktop = window.matchMedia('(min-width: 750px)').matches;
    const openForm = document.getElementById('FacetFiltersFormOpenDesktop');
    if (!openForm) return;

    openForm.querySelectorAll('input, select, textarea').forEach((el) => {
      if (el.hasAttribute('data-open-facet-search')) {
        el.disabled = false;
        return;
      }
      el.disabled = !desktop;
    });

    document.querySelectorAll('[data-facet-open-desktop-sync-root]').forEach((root) => {
      root.querySelectorAll('input, select, textarea').forEach((el) => {
        el.disabled = desktop;
      });
    });
  }
}

customElements.define('facets-open-desktop', FacetsOpenDesktop);

window.syncOpenDesktopFacetInputs = FacetsOpenDesktop.syncDuplicateFacetInputs;
